import { generate } from "@ant-design/colors";
import { ArrowUpOutlined, UserOutlined } from "@ant-design/icons";
import {
  Button,
  Spin,
  Input,
  Layout,
  theme,
  Empty,
  Space,
  Tooltip,
  Avatar,
  GlobalToken,
  Row,
  Col,
} from "antd";
import React, { useState, useRef, useEffect } from "react";
import { MarkdownRenderer } from "src/components/MarkdownRenderer";
import { SparkleIcon } from "src/components/SparkleIcon";
import { VzblIcon } from "src/components/VzblIcon";
import { useApplicationContext } from "src/hooks/useApplication";
import { useColorScheme } from "src/hooks/useColorScheme";
import { SearchState } from "src/search/types";
import styles from "./Chat.module.css";
const { TextArea } = Input;

export interface Message {
  text: string;
  sender: "user" | "bot";
  prompts?: Array<{ title: string; prompt: string }>;
  copyEnabled?: boolean;
  isStreaming?: boolean;
}

export interface FollowUpPrompt {
  title: string;
  prompt: string;
}

export interface ResultSummaryResponse {
  summary: string;
  follow_up_prompts?: FollowUpPrompt[];
  conversationId?: string;
}

const neutralColors = (token: GlobalToken, colorScheme: string) => {
  return generate(token.colorBgLayout, {
    theme: colorScheme === "dark" ? "dark" : "default",
  });
};

const FollowUpPrompts = ({
  prompts,
  handleSend,
  className,
}: {
  prompts: FollowUpPrompt[];
  handleSend: (prompt: string) => Promise<void>;
  className?: string;
}) => {
  const { token } = theme.useToken();
  const colorScheme = useColorScheme();
  const neutral = neutralColors(token, colorScheme);
  return (
    <div className={className}>
      {prompts.map((prompt, index) => (
        <Tooltip title={prompt.title} key={index}>
          <Button
            type="dashed"
            style={{ border: 0, borderRadius: 20, backgroundColor: neutral[5] }}
            onClick={() => handleSend(prompt.prompt)}
          >
            <SparkleIcon style={{ marginRight: 5 }} /> {prompt.title}
          </Button>
        </Tooltip>
      ))}
    </div>
  );
};

const StreamingText = ({ text }: { text: string }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex);
      const progress = currentIndex / text.length;

      // Check for code blocks or table content to print in bulk
      const codeBlockMatch = remainingText.match(/^```[\S\s]*?```/);
      const tableMatch = remainingText.match(/^\|[\S\s]*?\n\n/);
      const listMatch = remainingText.match(/^[*-][\S\s]*?\n\n/);

      if (codeBlockMatch || tableMatch || listMatch) {
        const match = codeBlockMatch || tableMatch || listMatch;
        const chunk = match![0];
        const timeout = setTimeout(() => {
          setDisplayedText((prev) => prev + chunk);
          setCurrentIndex((prev) => prev + chunk.length);
        }, 100); // Slight pause before bulk printing
        return () => clearTimeout(timeout);
      }

      // Dynamic delay based on position in text
      let delay: number;
      let chunkSize = 1;

      if (progress < 0.05) {
        // First 15% - slow for readability
        delay = 5;
      } else if (progress < 0.15) {
        // Transition zone - medium speed
        delay = 2;
      } else if (progress < 0.8) {
        // Middle 60% - fast streaming
        // For the middle section, stream by sentence/phrase for better performance
        const sentenceMatch = remainingText.match(/^[^\n!.?]+[\n!.?]/);
        const phraseMatch = remainingText.match(/^[^,:;]+[,:;]/);
        
        if (sentenceMatch && sentenceMatch[0].length < 100) {
          // Stream complete sentences in the middle section
          chunkSize = sentenceMatch[0].length;
          delay = 50; // Small delay between sentences
        } else if (phraseMatch && phraseMatch[0].length < 50) {
          // Stream by phrases if no sentence found
          chunkSize = phraseMatch[0].length;
          delay = 30; // Small delay between phrases
        } else {
          // Very fast character streaming for middle section
          delay = 2;
        }
      } else if (progress < 0.9) {
        // Transition to ending - medium speed
        delay = 5;
      } else {
        // Last 10% - slower for conclusion
        delay = 8;
      }

      // Apply the streaming with calculated delay and chunk size
      const chunk = remainingText.slice(0, chunkSize);
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + chunk);
        setCurrentIndex((prev) => prev + chunkSize);
      }, delay);
      
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return <MarkdownRenderer>{displayedText}</MarkdownRenderer>;
};

export interface ChatProps {
  dataSources: Array<{ query: SearchState; format: "csv" }>;
  snippet?: SearchState;
  defaultPrompt?: string;
  startingPrompts?: String[];
  contextLabel?: React.ReactNode;
  inline?: boolean;
  title: string;
  handleSend: (
    prompt: string,
    messages: Message[],
    setMessages: (messages: Message[]) => void
  ) => Promise<void>;
  loading: boolean;
}

export const Chat = ({
  defaultPrompt,
  startingPrompts,
  contextLabel,
  inline = false,
  title,
  handleSend,
  loading,
}: ChatProps) => {
  const orgId = useApplicationContext().org_id!;
  const { token } = theme.useToken();
  const colorScheme = useColorScheme();
  const neutral = neutralColors(token, colorScheme);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>("");
  const contentRef = useRef<HTMLDivElement>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const isRunningPrompt = useRef(false);

  // Sample prompts for welcome screen
  const samplePrompts = [
    {
      title: "Performance Overview",
      description:
        "Get a comprehensive summary of team and project performance",
      prompt: `Provide a comprehensive analysis of ${title} including key performance indicators, trends, and notable patterns in the data.`,
    },
    {
      title: "Trend Analysis",
      description: "Identify patterns and trends over time",
      prompt: `Analyze trends and patterns in ${title} data over the current time period. Highlight any significant changes, growth patterns, or anomalies.`,
    },
    {
      title: "Key Insights",
      description: "Discover the most important findings in your data",
      prompt: `What are the top 3-5 most important insights from ${title}? Focus on actionable findings and significant patterns.`,
    },
    {
      title: "Comparative Analysis",
      description: "Compare metrics across different dimensions",
      prompt: `Compare ${title} metrics across different time periods, teams, or categories. Identify top performers and areas for improvement.`,
    },
  ];

  const onSendMessage = async (overridePrompt?: string) => {
    const promptToSend = overridePrompt || input.trim();
    if (loading || !promptToSend) return;

    const userMessage = {
      text: overridePrompt
        ? input.trim() || "Ask me about the data..."
        : input.trim(),
      sender: "user" as const,
      copyEnabled: false,
    };
    setMessages([...messages, userMessage]);
    setInput("");
    await handleSend(promptToSend, [...messages, userMessage], setMessages);
  };

  const handleSamplePromptClick = async (
    samplePrompt: (typeof samplePrompts)[0]
  ) => {
    const userMessage = {
      text: samplePrompt.title,
      sender: "user" as const,
      copyEnabled: false,
    };
    setMessages([userMessage]);
    await handleSend(samplePrompt.prompt, [userMessage], setMessages);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content} ref={contentRef}>
        {messages.length === 0 && !loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 24px",
              textAlign: "center",
              minHeight: "60%",
            }}
          >
            <div style={{ marginBottom: 32 }}>
              <Avatar
                size={64}
                style={{
                  backgroundColor: token.colorBgBase,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                  border: `2px solid ${token.colorBorderSecondary}`,
                }}
              >
                <SparkleIcon
                  style={{ fontSize: 28, color: token.colorPrimary }}
                />
              </Avatar>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 8,
                  color: token.colorTextHeading,
                  fontSize: 24,
                  fontWeight: 600,
                }}
              >
                Welcome to Data Assistant
              </h2>
              <p
                style={{
                  margin: 0,
                  color: token.colorTextSecondary,
                  fontSize: 16,
                  lineHeight: 1.5,
                  maxWidth: 480,
                }}
              >
                I can help you analyze your data and discover insights. Choose a
                sample question below or ask your own question.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 16,
                width: "100%",
                maxWidth: 800,
              }}
            >
              {samplePrompts.map((sample, index) => (
                <Button
                  key={index}
                  type="text"
                  onClick={() => handleSamplePromptClick(sample)}
                  style={{
                    height: "auto",
                    padding: 20,
                    textAlign: "left",
                    backgroundColor: token.colorFillQuaternary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                  className={styles.samplePromptCard}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: token.colorTextHeading,
                        marginBottom: 8,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <SparkleIcon
                        style={{
                          fontSize: 16,
                          marginRight: 8,
                          color: token.colorPrimary,
                        }}
                      />
                      {sample.title}
                    </div>
                    <div
                      style={{
                        color: token.colorTextSecondary,
                        fontSize: 14,
                        lineHeight: 1.4,
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {sample.description}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div key={index}>
            {message.sender === "user" ? (
              <div className={`${styles.message} ${styles.userMessage}`}>
                <div className={styles.messageIcon}>
                  <Avatar
                    size={30}
                    icon={<VzblIcon />}
                    style={{ backgroundColor: token.colorBgBase }}
                  />
                </div>
                <div
                  className={styles.messageText}
                  style={{ background: neutral[5] }}
                >
                  {message.text}
                </div>
              </div>
            ) : (
              <div className={styles.message}>
                <div className={styles.messageIcon}>
                  <Avatar
                    size={30}
                    style={{
                      backgroundColor: token.colorBgBase,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SparkleIcon
                      style={{ fontSize: 16, color: token.colorPrimary }}
                    />
                  </Avatar>
                </div>
                <div style={{ flex: 1 }}>
                  <div className={styles.botText}>
                    {message.isStreaming ? (
                      <StreamingText text={message.text} />
                    ) : (
                      <MarkdownRenderer>{message.text}</MarkdownRenderer>
                    )}
                  </div>
                  {message.prompts && message.prompts.length > 0 && (
                    <div className={styles.botPrompts}>
                      <FollowUpPrompts
                        prompts={message.prompts}
                        handleSend={(prompt) => onSendMessage(prompt)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        <div ref={endOfMessagesRef} />

        {loading && (
          <div className={styles.botMessage} style={{ opacity: 0.7 }}>
            <div className={styles.botIcon}>
              <Avatar
                size={30}
                style={{
                  backgroundColor: token.colorBgBase,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SparkleIcon
                  style={{ fontSize: 16, color: token.colorPrimary }}
                />
              </Avatar>
            </div>
            <div className={styles.botText}>
              <Spin size="small" style={{ marginRight: 10 }} />
              Thinking...
            </div>
          </div>
        )}
      </div>

      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          {contextLabel && (
            <div
              className={styles.contextLabel}
              style={{ color: token.colorTextSecondary }}
            >
              {contextLabel}
            </div>
          )}
          <div className={styles.textAreaContainer}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question about your data..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              className={styles.textArea}
              style={{
                borderRadius: token.borderRadiusLG,
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}
            />
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={() => onSendMessage()}
              loading={loading}
              className={styles.sendButton}
              style={{ color: token.colorPrimary }}
              disabled={!input.trim()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
